"use client";

import React from "react";
import { Layers, Sparkles, Bath, Wind, Shuffle, ShieldCheck, Maximize2, GitFork, FileCheck, ArrowRight, SlidersHorizontal as Sliders, DollarSign } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DecisionItem {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface CostFactorItem {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const keyDecisions: DecisionItem[] = [{
  title: "Fixture selection",
  description: "Choosing vanity faucets, shower valves, thermostatic trims, and freestanding tub spouts in durable finishes that resist hard water.",
  icon: Sparkles
}, {
  title: "Tile and material choices",
  description: "Balancing porcelain floor durability, natural stone slab feature walls, water-resistant grout systems, and solid surface counter materials.",
  icon: Layers
}, {
  title: "Shower and tub configuration",
  description: "Determining whether to build a low-curb walk-in shower, preserve an alcove soaking tub, or establish a dedicated wet-room enclosure.",
  icon: Bath
}, {
  title: "Ventilation upgrades",
  description: "Sizing high-capacity, ultra-quiet exhaust fans with humidity sensors to protect drywall, trim, and cabinetry from long-term moisture.",
  icon: Wind
}, {
  title: "Layout changes",
  description: "Evaluating whether to retain the existing footprint or reallocate floor space from adjoining closets for expanded vanity or shower space.",
  icon: Shuffle
}];
const costDrivers: CostFactorItem[] = [{
  title: "Fixture tier",
  description: "The spectrum between standard builder-grade fixtures and precision solid-brass European engineered valves and specialty trims.",
  icon: Sliders
}, {
  title: "Waterproofing scope",
  description: "Continuous bonded sheet membrane systems, custom linear drains, and pre-slope pan construction to help prevent water intrusion.",
  icon: ShieldCheck
}, {
  title: "Layout changes",
  description: "Shifting wall partitions, expanding into adjoining rooms, or modifying load-bearing perimeter walls.",
  icon: Shuffle
}, {
  title: "Square footage",
  description: "Total wall and floor square area dictating material volumes, demolition handling, subfloor prep, and surface tile installation time.",
  icon: Maximize2
}, {
  title: "Plumbing relocation complexity",
  description: "Trenching concrete slabs, coring floor joists, or redirecting central stack lines to reposition toilets, drains, and supply lines.",
  icon: GitFork
}, {
  title: "Permit complexity",
  description: "Local municipal inspection requirements, structural engineering sign-offs, and compliance with updated residential building and electrical codes.",
  icon: FileCheck
}];
export default function BathroomDecisions() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="bathroomdecisionsandcostdrivers" data-nav="light" ref={sectionRef} className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold border-primary/30 text-primary">
              Planning Guide
            </Badge>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
            Key Decisions &amp; Cost Drivers in Bathroom Remodeling
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Understanding the structural choices homeowners navigate and the primary engineering variables that govern bathroom construction scope.
          </p>
        </div>

        {/* Visual Callout Gallery */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 transition-all duration-700 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <AspectRatio ratio={16 / 9}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_walkin_shower.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_walkin_shower.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_walkin_shower.png-wvc-sizes" alt="Custom frameless glass walk-in shower with natural stone tile and brass fixtures in Brookhaven home" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </AspectRatio>
            <div className="p-4 bg-card border-t border-border">
              <p className="text-sm font-medium text-foreground">Custom wet-room configuration with concealed linear drain</p>
              <p className="text-xs text-muted-foreground mt-1">Waterproofing scope and subfloor prep dictate overall longevity.</p>
            </div>
          </div>

          <div className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <AspectRatio ratio={16 / 9}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/master_bathroom_double_vanity.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/master_bathroom_double_vanity.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/master_bathroom_double_vanity.png-wvc-sizes" alt="Double vanity cabinetry with quartz countertop, wall-mounted fixtures, and integrated LED mirrors" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </AspectRatio>
            <div className="p-4 bg-card border-t border-border">
              <p className="text-sm font-medium text-foreground">Plumbing wall reconfiguration and double-sink distribution</p>
              <p className="text-xs text-muted-foreground mt-1">Pipe routing behind drywall impacts carpentry and mechanical complexity.</p>
            </div>
          </div>
        </div>

        {/* Two Structured Bullet Lists */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* List 1: Key Decisions */}
          <Card className="border border-border/80 shadow-md bg-card/90">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Sliders className="size-5" />
                </div>
                <div>
                  <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                    Key Decisions Homeowners Face
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Design, comfort, and architectural preferences to define before construction
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-5" aria-label="Key layout, material, and structural decisions">
                {keyDecisions.map((item, i) => {
                const IconComp = item.icon;
                return <li key={item.title} data-index={i} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/40 transition-colors">
                      <div className="mt-1 flex-shrink-0 size-8 rounded-md bg-accent flex items-center justify-center text-accent-foreground">
                        <IconComp className="size-4 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-foreground tracking-tight">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </li>;
              })}
              </ul>
            </CardContent>
          </Card>

          {/* List 2: Cost-Driving Factors */}
          <Card className="border border-border/80 shadow-md bg-card/90">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary text-secondary-foreground">
                  <DollarSign className="size-5" />
                </div>
                <div>
                  <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                    Cost-Driving Factors
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mechanical, material, and structural variables influencing overall project scope
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-5" aria-label="Cost-driving factors in bathroom remodeling">
                {costDrivers.map((factor, i) => {
                const IconComp = factor.icon;
                return <li key={factor.title} data-index={i} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/40 transition-colors">
                      <div className="mt-1 flex-shrink-0 size-8 rounded-md bg-secondary/10 flex items-center justify-center text-secondary">
                        <IconComp className="size-4 text-secondary" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-foreground tracking-tight">
                          {factor.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {factor.description}
                        </p>
                      </div>
                    </li>;
              })}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Informative Closing Footnote & Call to Action */}
        <div className={`mt-12 p-6 md:p-8 rounded-xl bg-muted/60 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-2xl space-y-1">
            <h4 className="text-base font-semibold text-foreground">
              Every Brookhaven home carries unique framing and plumbing pathways.
            </h4>
            <p className="text-sm text-muted-foreground">
              A comprehensive on-site architectural evaluation clarifies your feasibility, rough-in paths, and structural parameters before design commitment.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactRequestAnEstimatePage">
                Schedule a Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}